import React, { useEffect, useState } from 'react';
import { Sparkles, Trash2 } from 'lucide-react';

import { cn } from '../../../lib/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/primitives/Dialog';
import type { PlanSession } from '../../types/coursePlan';
import { type SessionConfig } from '../../types/sessionArchetypes';
import { formatSessionNotes, parseSessionNotes } from '../../lib/sessionNotesHelpers';
import {
  getArchetypeSuggestionHint,
  resolveSessionConfig,
} from '../../lib/hitoArchetypeUi';
import { SessionArchetypeSelector } from './SessionArchetypeSelector';
import { SessionContextInferencePanel } from './SessionContextInferencePanel';

export interface HitoConfigDraft {
  title: string;
  foco: string;
  evidencia: string;
  plannedDate?: string;
  config: SessionConfig;
}

interface HitoConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session: PlanSession;
  hitoIndex: number;
  isGenerating: boolean;
  subjectTag?: string;
  onSave: (patch: Partial<PlanSession>) => void;
  onGenerate: (draft: HitoConfigDraft) => Promise<{ ok: boolean; error?: string }>;
  onDelete?: () => void;
}

const FIELD_CLASS =
  'w-full rounded-lg border border-transparent bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 outline-none transition placeholder:text-gray-400 focus:border-gray-200 focus:ring-1 focus:ring-gray-900';
const LABEL_CLASS = 'text-xs font-medium text-gray-500';

function draftFromSession(session: PlanSession): HitoConfigDraft {
  const { foco, evidencia } = parseSessionNotes(session.notes);
  return {
    title: session.title,
    foco,
    evidencia,
    plannedDate: session.plannedDate,
    config: resolveSessionConfig(session),
  };
}

function draftToPatch(draft: HitoConfigDraft): Partial<PlanSession> {
  return {
    title: draft.title.trim(),
    notes: formatSessionNotes(draft.foco, draft.evidencia),
    plannedDate: draft.plannedDate || undefined,
    config: draft.config,
  };
}

function briefFromDraft(draft: HitoConfigDraft): string {
  const notes = formatSessionNotes(draft.foco, draft.evidencia)?.trim() ?? '';
  if (notes.length >= 8) return notes;
  const title = draft.title.trim();
  if (title.length >= 8) return title;
  return notes || title;
}

export function HitoConfigDialog({
  open,
  onOpenChange,
  session,
  hitoIndex,
  isGenerating,
  subjectTag = 'matematica',
  onSave,
  onGenerate,
  onDelete,
}: HitoConfigDialogProps) {
  const [draft, setDraft] = useState<HitoConfigDraft>(() => draftFromSession(session));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDraft(draftFromSession(session));
      setError(null);
    }
  }, [open, session]);

  const handleSave = () => {
    onSave(draftToPatch(draft));
    onOpenChange(false);
  };

  const handleGenerate = async () => {
    if (briefFromDraft(draft).length < 8) {
      setError('Escribe un foco o título más detallado (mínimo 8 caracteres) antes de generar.');
      return;
    }

    setError(null);
    onSave(draftToPatch(draft));

    const result = await onGenerate(draft);
    if (result.ok) {
      onOpenChange(false);
      return;
    }
    setError(result.error ?? 'No se pudo generar el detalle de la sesión.');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto border-gray-200 bg-white p-0">
        <div className="space-y-6 px-6 pb-6 pt-5">
          <DialogHeader className="space-y-1.5 text-left">
            <DialogTitle className="text-base font-semibold text-gray-900">
              Configurar hito {hitoIndex + 1}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-gray-500">
              Define el foco pedagógico del hito y el formato de sesión. La IA generará la línea de
              tiempo.
            </DialogDescription>
          </DialogHeader>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <div className="space-y-5">
            <label className="block space-y-2">
              <span className={LABEL_CLASS}>Título del hito</span>
              <input
                className={FIELD_CLASS}
                value={draft.title}
                onChange={(event) => setDraft((current) => ({ ...current, title: event.target.value }))}
                placeholder="Ej: Explorando patrones rítmicos"
              />
            </label>

            <label className="block space-y-2">
              <span className={LABEL_CLASS}>Foco pedagógico</span>
              <textarea
                className={cn(FIELD_CLASS, 'min-h-[88px] resize-y')}
                value={draft.foco}
                onChange={(event) => setDraft((current) => ({ ...current, foco: event.target.value }))}
                placeholder="¿Qué aprendizaje o habilidad concentra este hito?"
              />
              {open ? (
                <SessionContextInferencePanel
                  foco={draft.foco}
                  evidencia={draft.evidencia}
                  title={draft.title}
                  sessionConfig={draft.config}
                  subjectTag={subjectTag}
                />
              ) : null}
            </label>

            <label className="block space-y-2">
              <span className={LABEL_CLASS}>Evidencia de avance</span>
              <textarea
                className={cn(FIELD_CLASS, 'min-h-[88px] resize-y')}
                value={draft.evidencia}
                onChange={(event) => setDraft((current) => ({ ...current, evidencia: event.target.value }))}
                placeholder="¿Cómo se observará el avance?"
              />
            </label>

            <label className="block space-y-2">
              <span className={LABEL_CLASS}>Fecha planificada (opcional)</span>
              <input
                type="date"
                className={cn(FIELD_CLASS, 'max-w-[180px]')}
                value={draft.plannedDate ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    plannedDate: event.target.value || undefined,
                  }))
                }
              />
            </label>

            <SessionArchetypeSelector
              variant="compact"
              tone="swiss"
              value={draft.config}
              onChange={(config) => setDraft((current) => ({ ...current, config }))}
              disabled={isGenerating}
              helperText={getArchetypeSuggestionHint(session.curriculumSlice?.dimensionDominante)}
            />
          </div>
        </div>

        <DialogFooter className="flex flex-col gap-3 border-t border-gray-100 bg-gray-50/60 px-6 py-4 sm:flex-row sm:justify-between">
          {onDelete ? (
            <button
              type="button"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-gray-500 transition hover:text-red-600"
              onClick={() => {
                onOpenChange(false);
                onDelete();
              }}
              disabled={isGenerating}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Eliminar hito
            </button>
          ) : (
            <span />
          )}

          <div className="flex flex-wrap gap-2 sm:justify-end">
            <button
              type="button"
              className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 transition hover:bg-gray-50"
              onClick={handleSave}
              disabled={isGenerating}
            >
              Guardar
            </button>
            <button
              type="button"
              className="inline-flex items-center rounded-lg bg-gray-900 px-4 py-2 text-xs font-medium text-white transition hover:bg-gray-800 disabled:opacity-50"
              onClick={handleGenerate}
              disabled={isGenerating}
            >
              <Sparkles className="mr-1.5 h-3.5 w-3.5" />
              {isGenerating ? 'Generando…' : 'Generar detalle con IA'}
            </button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
